import { forwardRef } from 'react'
import { inject, observer } from 'mobx-react'
import MuiAlert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import { ThemeProvider } from '@mui/material/styles'

import muiTheme from '../../themes/muiTheme'

const DEFAULT_AUTO_HIDE = 6000

const Alert = forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant='filled' {...props} />
})

/**
 * App-wide toast, driven by `store.toast`.
 *
 * Structure and colors follow the institutional portal's toast so status messaging reads the same
 * across both products: filled alert, anchored top-center, dismissible, auto-hiding after six
 * seconds. Raise one with `store.showToast(...)` from anywhere.
 *
 * When the toast carries an action, the whole surface is clickable and the action label reads as
 * a link inside it.
 */
const ToastBar = ({ store }) => {
  const { toast, hideToast } = store
  const { open, severity, message, actionLabel, onAction, autoHideDuration } = toast

  // MUI defaults `severity` to success, which would flash green on the way out.
  if (!severity) return null

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return
    hideToast()
  }

  /**
   * Dismisses without running the toast's action.
   *
   * The dismiss control sits inside the alert, so its click would otherwise bubble to the
   * alert's own onClick and take an actionable toast's action — navigating the user away when
   * they asked to get rid of it.
   */
  const handleCloseClick = event => {
    event.stopPropagation()
    hideToast()
  }

  const handleAction = () => {
    if (!onAction) return
    hideToast()
    onAction()
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <Snackbar
        open={open}
        onClose={handleClose}
        autoHideDuration={autoHideDuration || DEFAULT_AUTO_HIDE}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert
          severity={severity}
          onClose={handleCloseClick}
          onClick={onAction ? handleAction : undefined}
          sx={{
            width: '100%',
            cursor: onAction ? 'pointer' : 'default',
            fontSize: '16px',
            alignItems: 'center',
          }}>
          {message}
          {actionLabel && (
            // The alert itself handles the click; this only needs to read as a link.
            <span style={{ marginLeft: '6px', fontWeight: 700, textDecoration: 'underline' }}>
              {actionLabel}
            </span>
          )}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  )
}

export default inject('store')(observer(ToastBar))
